import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../../components/ui/button";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../../../components/ui/resizable";
import { useParams } from "react-router-dom";
import { useAudit } from "../../../contexts/auditContext";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import FileViewer from "../../extraction/viewExtraction/FileViewer";
import { X, Eye, Search, Check } from "lucide-react";
import { AuditSessionTabs } from "../../../components/audit/AuditSessionTabs";
import { Input } from "../../../components/ui/input";
import {
  AuditSession,
  sessionSchema,
} from "../../../models/audit/auditSession";
import AuditPageHeader from "../../../components/audit/AuditPageHeader";
import { AuditSampleView } from "../auditSampleTest/components/AuditSampleList";
import { fetchFastApi } from "../../../api/fastapi";
import { toast } from "sonner";
import { AuditDocumentsContent } from "../../../components/audit/AuditDocumentsContent";
import { DocumentProcessingToast } from "../../../components/audit/DocumentPipelineView";
import { useDocumentProcessingStatus } from "../../../hooks/useDocumentProcessingStatus";
import { useControlSessionStatus } from "../../../hooks/audit/useControlSessionStatus";
import { useHookCache } from "../../../lib/hookCache";
import { useSessionAnalyticsOwner } from "../../../hooks/audit/useSessionAnalyticsOwner";

export const AuditDocumentsPageV2 = () => {
  const { controlSessionId } = useParams();
  const {
    controlSession,
    updateCurrentDocument,
    documentUploadNonce,
    sessionKind,
  } = useAudit();
  const analyticsOwner = useSessionAnalyticsOwner();
  const { invalidate } = useHookCache();

  // Smart status hook owns 5s polling + invalidation of per-procedure caches
  // when a session transitions out of `processing`.
  useControlSessionStatus(controlSessionId, sessionKind, analyticsOwner);

  const { isProcessing: isDocumentsProcessing } = useDocumentProcessingStatus(
    controlSessionId ?? null,
    documentUploadNonce,
  );

  const firstControlSessionFetch = useRef(true);
  const sessionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // State for sample matching sidebar - now includes auditSession
  const [sampleMatchingSidebar, setSampleMatchingSidebar] = useState<{
    document: AuditSession["documents"][0];
    auditSession: AuditSession;
  } | null>(null);

  useEffect(() => {
    if (firstControlSessionFetch.current) {
      updateCurrentDocument(null);
      firstControlSessionFetch.current = false;
    }
  }, [updateCurrentDocument]);

  // When document processing finishes (isProcessing transitions true → false),
  // invalidate the control session's per-procedure caches to pick up new
  // documents / matches.
  const prevIsProcessingRef = useRef<boolean>(false);
  useEffect(() => {
    const wasProcessing = prevIsProcessingRef.current;
    prevIsProcessingRef.current = isDocumentsProcessing;
    if (!controlSessionId) return;
    if (wasProcessing && !isDocumentsProcessing) {
      void invalidate(["control-session", controlSessionId]);
    }
  }, [isDocumentsProcessing, controlSessionId, invalidate]);

  const enabledProcedureSessions = useMemo(() => {
    if (!controlSession) return [];
    return controlSession.procedureSessions
      .filter((session) => session.procedure.isEnabled)
      .sort((a, b) => a.procedure.procedureOrder - b.procedure.procedureOrder);
  }, [controlSession]);

  const scrollToSession = (index: number) => {
    const sessionElement = sessionRefs.current[index];
    if (sessionElement) {
      sessionElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleDocumentSelect = (
    doc: AuditSession["documents"][0],
    auditSession: AuditSession,
  ) => {
    setSampleMatchingSidebar({
      document: doc,
      auditSession,
    });
  };

  if (!controlSession) {
    return (
      <div className="w-full flex items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  const leftPanel = (
    <div className="h-full p-4">
      <div>
        <AuditPageHeader extraPageLabel="Documents" />
        <div className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <AuditSessionTabs activeTab="documents" v2={true} />
          </div>
        </div>
      </div>

      <div className="h-[calc(100vh-200px)] overflow-y-auto">
        <div className="">
          {enabledProcedureSessions.map((session, index) => (
            <div
              key={session.id}
              ref={(el) => (sessionRefs.current[index] = el)}
            >
              <AuditDocumentsContent
                procedureSessionId={session.id}
                controlSessionId={controlSessionId || ""}
                onDocumentSelect={handleDocumentSelect}
                currentIndex={index}
                totalSessions={enabledProcedureSessions.length}
                onNavigate={scrollToSession}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <ResizablePanelGroup
        autoSaveId="documents-view"
        direction="horizontal"
        className="h-screen w-full max-w-[2500px] mx-auto"
      >
        <ResizablePanel id="left-panel" order={1} defaultSize={60} minSize={30}>
          <div className="h-full">{leftPanel}</div>
        </ResizablePanel>

        {sampleMatchingSidebar && (
          <>
            <ResizableHandle withHandle />
            <ResizablePanel id="right-panel" order={2} defaultSize={40}>
              <SampleMatchingSidebar
                document={sampleMatchingSidebar.document}
                auditSession={sampleMatchingSidebar.auditSession}
                onClose={() => setSampleMatchingSidebar(null)}
              />
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
      <DocumentProcessingToast
        subjectId={controlSessionId ?? null}
        scope={
          sessionKind === "work_step" ? "work-step-session" : "control-session"
        }
        reconnectKey={documentUploadNonce}
      />
    </>
  );
};

const SampleMatchingSidebar = ({
  document,
  auditSession,
  onClose,
}: {
  document: AuditSession["documents"][0];
  auditSession: AuditSession;
  onClose: () => void;
}) => {
  const { fetchPresignedUrl } = useAudit();
  const { setEntry } = useHookCache();
  const [selectedSamples, setSelectedSamples] = useState<string[]>([]);
  const [isViewingDocument, setIsViewingDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [sampleSearchTerm, setSampleSearchTerm] = useState("");

  // Get current sample assignments for this document
  const currentSampleIds = useMemo(() => {
    const docId = document.documentId || document.id;
    const assignments: string[] = [];

    for (const sourceRow of auditSession?.sourceRows || []) {
      for (const matchItem of sourceRow.matchItems || []) {
        if (matchItem.sourceDocument.id === docId) {
          assignments.push(sourceRow.id);
        }
      }
    }
    return assignments;
  }, [document, auditSession?.sourceRows]);

  useEffect(() => {
    const fetchDocumentUrl = async () => {
      const urlData = await fetchPresignedUrl(document.id, auditSession.id);
      setDocumentUrl(urlData?.url || null);
    };
    fetchDocumentUrl();
  }, [document, fetchPresignedUrl, auditSession.id]);

  useEffect(() => {
    setSelectedSamples(currentSampleIds);
  }, [currentSampleIds]);

  // Filter samples based on search term
  const filteredSamples = useMemo(() => {
    if (!sampleSearchTerm.trim() || !auditSession?.sourceRows) {
      return auditSession?.sourceRows || [];
    }

    const searchLower = sampleSearchTerm.toLowerCase().trim();

    return auditSession.sourceRows.filter((sourceRow) => {
      // Check if search matches sample number
      const sampleNumber = sourceRow.sampleNumber || 0;
      if (
        String(sampleNumber).includes(searchLower) ||
        `sample ${sampleNumber}`.includes(searchLower)
      ) {
        return true;
      }

      // Search in all data fields
      for (const key in sourceRow.data) {
        const value = String(sourceRow.data[key] || "").toLowerCase();
        if (value.includes(searchLower)) {
          return true;
        }
      }
      return false;
    });
  }, [auditSession?.sourceRows, sampleSearchTerm]);

  const handleSampleToggle = (sampleId: string, checked: boolean) => {
    const newSelectedSamples = checked
      ? [...selectedSamples, sampleId]
      : selectedSamples.filter((id) => id !== sampleId);

    setSelectedSamples(newSelectedSamples);
    handleSave(newSelectedSamples);
  };

  const handleSave = async (samplesToSave: string[] = selectedSamples) => {
    try {
      const response = await fetchFastApi({
        method: "POST",
        path: `/matching/match-session/${auditSession?.id}/document/${document.documentId}/update-assigned-samples`,
        body: {
          assigned_samples: samplesToSave,
        },
      });
      const newAuditSession = sessionSchema.parse(response.session);
      setEntry<AuditSession>(
        ["procedure-session", newAuditSession.id],
        () => newAuditSession,
      );
      toast.success(`Sample assignments updated for ${document.name}`);
    } catch (error) {
      console.error("Error updating sample assignments:", error);
      toast.error("Error updating sample assignments");
    }
  };

  if (isViewingDocument) {
    return (
      <div className="h-screen flex flex-col border-l bg-white overflow-hidden">
        <div className="flex justify-between items-center p-6 shrink-0">
          <div className="flex items-center gap-2">
            <img src="/logo_orange_bg.svg" alt="logo" className="w-8" />
            <div className="font-semibold">
              {document.fetchableDocument?.fileName || "Document Viewer"}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setIsViewingDocument(false);
            }}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <FileViewer
            signedUrl={documentUrl || ""}
            fileType={document.fetchableDocument?.fileType || "pdf"}
            fileName={document.fetchableDocument?.fileName}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col border-l bg-white overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-semibold">
          {document.name} - Select sample to match this document
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Document Preview */}
      <div className="p-4 border-b">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setIsViewingDocument(true)}
        >
          <Eye className="h-4 w-4 mr-2" />
          View Document
        </Button>
      </div>

      {auditSession?.procedure.procedureBasis == "sample" && (
        <>
          {/* Search Input */}
          <div className="p-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Search samples by number or data..."
                value={sampleSearchTerm}
                onChange={(e) => setSampleSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Sample List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Search Results Count */}
            {sampleSearchTerm.trim() && (
              <div className="text-sm text-stone-500 mb-2">
                {filteredSamples.length} sample
                {filteredSamples.length !== 1 ? "s" : ""} found
              </div>
            )}

            {filteredSamples.length === 0 && sampleSearchTerm.trim() ? (
              <div className="text-center text-stone-500 py-8">
                <div className="text-sm">No samples match your search</div>
              </div>
            ) : (
              filteredSamples
                .sort((a, b) => {
                  const aSelected = selectedSamples.includes(a.id);
                  const bSelected = selectedSamples.includes(b.id);

                  // First sort by selection status (matched samples first)
                  if (aSelected && !bSelected) return -1;
                  if (!aSelected && bSelected) return 1;

                  // Then sort by sample number within each group
                  return (a.sampleNumber || 0) - (b.sampleNumber || 0);
                })
                .map((sample) => {
                  const sourceRow = sample;
                  if (!sourceRow) return null;

                  const isSelected = selectedSamples.includes(sample.id);

                  return (
                    <div
                      key={sample.id}
                      className={`border cursor-pointer transition-colors ${isSelected ? "border-green-500 bg-green-50 hover:bg-green-100" : "border-stone-200 hover:border-stone-300 hover:bg-stone-100"}`}
                      onClick={() => handleSampleToggle(sample.id, !isSelected)}
                    >
                      <div className="m-2 bg-white">
                        <div className="p-4">
                          {/* Header with checkbox and sample name */}
                          <div className="flex items-center gap-3 mb-4">
                            <div
                              className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
                                isSelected
                                  ? "bg-green-800"
                                  : "border border-stone-200 bg-white"
                              }`}
                            >
                              {isSelected && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div className="flex px-1 py-0.5 bg-stone-100 rounded-md">
                              <span className="text-xs">
                                Sample {sample.sampleNumber}
                              </span>
                            </div>
                          </div>
                          <AuditSampleView
                            auditSession={auditSession}
                            sourceRow={sourceRow}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </>
      )}
    </div>
  );
};
